/** Checking rule input from the API before it is stored. */
import { isActionKind } from '@orgus/protocol';
import type { PolicyDecision, PolicyRuleInput } from '@orgus/protocol';

const DECISIONS: readonly PolicyDecision[] = ['allow', 'ask', 'deny'];
const MAX = 500;

export type Validated<T> = { ok: true; value: T } | { ok: false; error: string };

const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);

function optText(x: unknown, name: string): Validated<string | undefined> {
  if (x === undefined || x === null || x === '') return { ok: true, value: undefined };
  if (typeof x !== 'string') return { ok: false, error: `${name} must be text.` };
  const t = x.trim();
  if (t.length > MAX) return { ok: false, error: `${name} must be at most ${MAX} characters.` };
  if (/[\u0000-\u0008\u000b-\u001f]/.test(t)) return { ok: false, error: `${name} has control characters.` };
  return { ok: true, value: t || undefined };
}

/** A rule from a form or the API: a scope, at least one thing to match, and a decision. */
export function validateRuleInput(x: unknown): Validated<PolicyRuleInput> {
  if (!isObj(x)) return { ok: false, error: 'A rule must be an object.' };
  const scope = x.scope;
  if (!isObj(scope) || (scope.kind !== 'org' && scope.kind !== 'project')) return { ok: false, error: 'Pick a scope: the whole org or one project.' };
  if (scope.kind === 'project' && (typeof scope.projectId !== 'string' || !scope.projectId)) return { ok: false, error: 'A project rule needs a project.' };
  if (!DECISIONS.includes(x.decision as PolicyDecision)) return { ok: false, error: 'The decision must be allow, ask or deny.' };
  const m = isObj(x.match) ? x.match : {};
  const tool = optText(m.tool, 'Tool');
  const pattern = optText(m.pattern, 'Pattern');
  const model = optText(m.model, 'Model');
  for (const v of [tool, pattern, model]) if (!v.ok) return v;
  let action: PolicyRuleInput['match']['action'];
  if (m.action !== undefined && m.action !== null && m.action !== '') {
    if (!isActionKind(m.action)) return { ok: false, error: 'That action is not an Orgus action.' };
    action = m.action;
  }
  const match: PolicyRuleInput['match'] = {};
  if (tool.ok && tool.value) match.tool = tool.value;
  if (action) match.action = action;
  if (pattern.ok && pattern.value) match.pattern = pattern.value;
  if (model.ok && model.value) match.model = model.value;
  if (!Object.keys(match).length) return { ok: false, error: 'Say what the rule matches: a tool, an action, a pattern or a model.' };
  if (match.tool && match.action) return { ok: false, error: 'A rule matches either a tool call or an Orgus action, not both.' };
  const reason = optText(x.reason, 'Reason');
  if (!reason.ok) return reason;
  if (x.enabled !== undefined && typeof x.enabled !== 'boolean') return { ok: false, error: 'Enabled must be true or false.' };
  return {
    ok: true,
    value: {
      scope: scope.kind === 'project' ? { kind: 'project', projectId: scope.projectId as string } : { kind: 'org' },
      match,
      decision: x.decision as PolicyDecision,
      ...(reason.value ? { reason: reason.value } : {}),
      ...(x.enabled !== undefined ? { enabled: x.enabled as boolean } : {}),
    },
  };
}

/** The reason a rule gives when its author wrote none. */
export function defaultReason(input: Pick<PolicyRuleInput, 'decision' | 'scope'>): string {
  const where = input.scope.kind === 'project' ? 'a project' : 'an org';
  if (input.decision === 'allow') return `Allowed by ${where} rule.`;
  if (input.decision === 'deny') return `Refused by ${where} rule.`;
  return `${where[0]!.toUpperCase()}${where.slice(1)} rule asks a person first.`;
}
